/**
 * Snapshot storage — localStorage-backed persistence for PipelineSnapshots.
 *
 * Why localStorage: snapshots are admin-local diagnostics, no need for a
 * server round-trip. The compact form (~50-150KB per snapshot) fits well
 * within the 5MB localStorage budget — we keep a soft cap (default 20)
 * and prune oldest first. The cap is admin-configurable via Settings.
 *
 * Keys:
 *   governance:snapshot:index  → JSON array of SnapshotIndexEntry
 *   governance:snapshot:<id>   → JSON-serialized PipelineSnapshot
 *   governance:snapshot:cap    → number (admin override of DEFAULT_SNAPSHOT_CAP)
 */

import { error } from '../../../utils/logger';
import type { PipelineSnapshot } from './types';
const INDEX_KEY = 'governance:snapshot:index';
const ITEM_PREFIX = 'governance:snapshot:';
const CAP_KEY = 'governance:snapshot:cap';
export const DEFAULT_SNAPSHOT_CAP = 20;
export const MIN_SNAPSHOT_CAP = 5;
export const MAX_SNAPSHOT_CAP = 100;

/** Read the configured snapshot cap, clamped to the allowed range. */
export function getSnapshotCap(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_SNAPSHOT_CAP;
  const raw = localStorage.getItem(CAP_KEY);
  if (!raw) return DEFAULT_SNAPSHOT_CAP;
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_SNAPSHOT_CAP;
  return Math.max(MIN_SNAPSHOT_CAP, Math.min(MAX_SNAPSHOT_CAP, Math.floor(n)));
}

/**
 * Persist a new snapshot cap (clamped). Pass `null` (or omit) to reset to default.
 * Does NOT immediately prune existing snapshots — pruning happens on next save.
 */
export function setSnapshotCap(cap: number | null): void {
  if (typeof localStorage === 'undefined') return;
  if (cap === null) {
    localStorage.removeItem(CAP_KEY);
    return;
  }
  const clamped = Math.max(MIN_SNAPSHOT_CAP, Math.min(MAX_SNAPSHOT_CAP, Math.floor(cap)));
  localStorage.setItem(CAP_KEY, String(clamped));
}
export interface SnapshotIndexEntry {
  id: string;
  createdAt: string;
  label?: string;
  /** Convenience preview values shown in the picker without loading the full snapshot. */
  totalQuestions: number;
  totalIssues: number;
  passCount: number;
  failCount: number;
  reviewCount: number;
}
function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
function readIndex(): SnapshotIndexEntry[] {
  if (typeof localStorage === 'undefined') return [];
  return safeParse<SnapshotIndexEntry[]>(localStorage.getItem(INDEX_KEY), []);
}
function writeIndex(index: SnapshotIndexEntry[]): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(INDEX_KEY, JSON.stringify(index));
}

/** Return all snapshot index entries, newest first. */
export function listSnapshots(): SnapshotIndexEntry[] {
  return [...readIndex()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * Huidige snapshotvorm. Verhoog dit nummer zodra `PipelineSnapshot` van vorm
 * verandert; oudere snapshots worden dan geweigerd in plaats van verkeerd
 * vergeleken.
 */
export const SNAPSHOT_SCHEMA_VERSION = 1 as const;

/** Load a full snapshot by id; returns null if missing, corrupt or outdated. */
export function loadSnapshot(id: string): PipelineSnapshot | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(`${ITEM_PREFIX}${id}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PipelineSnapshot>;

    // `schemaVersion` werd wél weggeschreven maar nooit gelezen: de waarde
    // werd blind naar `PipelineSnapshot` gecast. Een snapshot uit een oudere
    // vorm miste dan velden, en het vergelijken rekende met `undefined` —
    // `0 - undefined` geeft `NaN`, en dat verscheen als een verschil dat er
    // geldig uitzag. Erger dan een foutmelding: een diff waar een beheerder
    // conclusies aan verbindt.
    if (parsed.schemaVersion !== SNAPSHOT_SCHEMA_VERSION) {
      error(`Snapshot ${id} heeft schemaversie ${parsed.schemaVersion ?? 'onbekend'} ` + `(verwacht ${SNAPSHOT_SCHEMA_VERSION}) en wordt niet gebruikt. ` + 'Vergelijken met een andere vorm levert getallen op die niets betekenen.');
      return null;
    }
    if (!parsed.aggregate || !Array.isArray(parsed.rows)) {
      error(`Snapshot ${id} mist verplichte velden (aggregate/rows) en wordt niet gebruikt.`);
      return null;
    }
    return parsed as PipelineSnapshot;
  } catch (err) {
    error('Snapshot load failed for id', id, err);
    return null;
  }
}

/**
 * Save a snapshot. Updates the index and prunes oldest snapshots if the
 * cap is exceeded. Returns true on success.
 */
export function saveSnapshot(snap: PipelineSnapshot): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    localStorage.setItem(`${ITEM_PREFIX}${snap.id}`, JSON.stringify(snap));
    const index = readIndex().filter(e => e.id !== snap.id);
    const entry: SnapshotIndexEntry = {
      id: snap.id,
      createdAt: snap.createdAt,
      ...(snap.label !== undefined ? {
        label: snap.label
      } : {}),
      totalQuestions: snap.aggregate.totalQuestions,
      totalIssues: snap.aggregate.totalIssues,
      passCount: snap.aggregate.verdicts.PASS,
      failCount: snap.aggregate.verdicts.FAIL,
      reviewCount: snap.aggregate.verdicts.REVIEW_NEEDED
    };
    index.push(entry);

    // Prune: keep newest N snapshots, where N is the admin-configurable cap.
    const cap = getSnapshotCap();
    index.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const keep = index.slice(0, cap);
    const drop = index.slice(cap);
    for (const d of drop) {
      localStorage.removeItem(`${ITEM_PREFIX}${d.id}`);
    }
    writeIndex(keep);
    return true;
  } catch (err) {
    error('Snapshot save failed', err);
    return false;
  }
}

/** Delete a snapshot by id. */
export function deleteSnapshot(id: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    localStorage.removeItem(`${ITEM_PREFIX}${id}`);
    writeIndex(readIndex().filter(e => e.id !== id));
    return true;
  } catch (err) {
    error('Snapshot delete failed for id', id, err);
    return false;
  }
}

/** Delete every stored snapshot. Useful for clean-slate dev cycles. */
export function clearAllSnapshots(): void {
  if (typeof localStorage === 'undefined') return;
  for (const entry of readIndex()) {
    localStorage.removeItem(`${ITEM_PREFIX}${entry.id}`);
  }
  localStorage.removeItem(INDEX_KEY);
}